import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom, Subscription } from 'rxjs';
import { DataService } from '../data.service';

/**
 *  HIBAJELENTÉS  ·  a saját bejelentések, új bejelentés, és egy jegy idővonala.
 *
 *  Egy komponens, három nézet (útvonal szerint): `/hibajegy` a lista, `/hibajegy/uj` az
 *  űrlap, `/hibajegy/:id` a jegy. Egy komponens, mert a három ugyanazt a kilétet és
 *  intézményt használja, és a beküldés után a lista frissítése így nem kerül kérésbe.
 *
 *  A kilétet a KISZOLGÁLÓ dönti el a tokenből – innen csak az intézményt és azt küldjük,
 *  milyen szerepben lépett be a felhasználó (dolgozó/gondviselő), ha mindkettő lehetne.
 *  A kontextust (verzió, böngésző, szerep) is a kiszolgáló írja a jegyhez; mi csak azt
 *  adjuk, amit ő nem tud: melyik képernyőről jött a felhasználó.
 */
@Component({
  selector: 'app-hibajegy',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './hibajegy.component.html',
  styleUrls: ['./hibajegy.component.scss'],
})
export class HibajegyComponent implements OnInit, OnDestroy {
  nezet: 'lista' | 'uj' | 'jegy' = 'lista';

  intezmeny = '';
  szerep = 'munkatars';
  admin = false;
  osszes = false;

  toltodik = false;
  hiba = '';
  jegyek: any[] = [];

  // Új bejelentés
  uj = { targy: '', leiras: '', kategoria: '' };
  ujFajl: File | null = null;
  honnan = '';
  kuld = false;

  // Egy jegy
  jegy: any = null;
  csatolmanyok: any[] = [];
  esemenyek: any[] = [];
  jog: any = {};
  valasz = '';
  valaszFajl: File | null = null;
  valaszKuld = false;

  frissul = false;

  readonly maxFajl = 10 * 1024 * 1024;
  readonly kategoriak = [
    { kod: 'hiba', nev: 'Hiba' },
    { kod: 'kerdes', nev: 'Kérdés' },
    { kod: 'javaslat', nev: 'Javaslat' },
  ];

  private sub?: Subscription;

  constructor(private http: HttpClient, private data: DataService,
              private route: ActivatedRoute, private router: Router) {}

  async ngOnInit() {
    const int = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(int?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    const mod = await this.data.getFromStorage('loginmode');
    this.szerep = mod === 'gondviselo' ? 'gondviselo' : 'munkatars';
    /*  Melyik képernyőről jött? Az alkalmazás keretét (app.component) minden oldalváltáskor
     *  feljegyzi az utolsó, nem hibajelentés-oldalt – a bejelentéshez ez a leghasznosabb nyom. */
    try { this.honnan = sessionStorage.getItem('hibajegy-honnan') || ''; } catch { this.honnan = ''; }
    this.sub = this.route.paramMap.subscribe((p) => this.utvonal(p.get('id')));
  }

  ngOnDestroy() { this.sub?.unsubscribe(); }

  private params(extra: Record<string, string> = {}): HttpParams {
    let p = new HttpParams().set('intezmeny', this.intezmeny).set('szerep', this.szerep);
    for (const [k, v] of Object.entries(extra)) p = p.set(k, v);
    return p;
  }

  private async utvonal(id: string | null) {
    this.hiba = '';
    if (!id) { this.nezet = 'lista'; await this.listaBetolt(); return; }
    if (id === 'uj') { this.nezet = 'uj'; return; }
    this.nezet = 'jegy';
    await this.jegyBetolt(id);
  }

  async listaBetolt() {
    this.toltodik = true;
    try {
      const r: any = await firstValueFrom(this.http.get('/api/hibajegy/jegyek',
        { params: this.params(this.osszes ? { osszes: '1' } : {}) }));
      this.jegyek = r?.jegyek || [];
      this.admin = !!r?.admin;
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A bejelentések nem tölthetők be.';
    } finally {
      this.toltodik = false;
    }
  }

  async osszesValt() {
    this.osszes = !this.osszes;
    await this.listaBetolt();
  }

  megnyit(j: any) { this.router.navigate(['/hibajegy', j.id]); }
  ujBejelentes() { this.router.navigate(['/hibajegy', 'uj']); }
  vissza() { this.router.navigate(['/hibajegy']); }

  fajlValaszt(esemeny: Event, hova: 'uj' | 'valasz') {
    const input = esemeny.target as HTMLInputElement;
    const f = input?.files?.[0] || null;
    if (f && f.size > this.maxFajl) {
      this.data.sendToast('A melléklet legfeljebb 10 MB lehet.', 'error');
      input.value = '';
      return;
    }
    if (f && !/^(image\/(png|jpeg|gif|webp)|application\/pdf)$/.test(f.type)) {
      this.data.sendToast('Csak képernyőkép (PNG, JPG, GIF, WEBP) vagy PDF csatolható.', 'error');
      input.value = '';
      return;
    }
    if (hova === 'uj') this.ujFajl = f; else this.valaszFajl = f;
  }

  get ujKuldheto(): boolean {
    return !!this.uj.targy.trim() && !!this.uj.leiras.trim() && !this.kuld;
  }

  async bekuld() {
    if (!this.ujKuldheto) return;
    this.kuld = true;
    try {
      const fd = new FormData();
      fd.append('targy', this.uj.targy.trim());
      fd.append('leiras', this.uj.leiras);
      if (this.uj.kategoria) fd.append('kategoria', this.uj.kategoria);
      fd.append('alrendszer', String((await this.data.getFromStorage('currentMode')) || 'SULINFORM'));
      fd.append('honnan', this.honnan);
      if (this.ujFajl) fd.append('file', this.ujFajl, this.ujFajl.name);
      const r: any = await firstValueFrom(this.http.post('/api/hibajegy/jegyek', fd, { params: this.params() }));
      this.data.sendToast(r?.uzenet || 'A bejelentést megkaptuk.', r?.elkuldve ? 'success' : 'info');
      this.uj = { targy: '', leiras: '', kategoria: '' };
      this.ujFajl = null;
      if (r?.jegy?.id) this.router.navigate(['/hibajegy', r.jegy.id]);
      else this.vissza();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A bejelentés nem sikerült.', 'error');
    } finally {
      this.kuld = false;
    }
  }

  async jegyBetolt(id: string) {
    this.toltodik = true;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/hibajegy/jegyek/${encodeURIComponent(id)}`,
        { params: this.params() }));
      this.jegy = r?.jegy;
      this.csatolmanyok = r?.csatolmanyok || [];
      this.esemenyek = r?.esemenyek || [];
      this.jog = r?.jog || {};
      /*  A megnyitás olvasottá tette a jegyet: a menü jelvénye ne a következő percben
       *  tűnjön el, hanem most. */
      window.dispatchEvent(new CustomEvent('hibajegy-olvasva'));
    } catch (e: any) {
      this.jegy = null;
      this.hiba = e?.error?.error || 'A bejelentés nem tölthető be.';
    } finally {
      this.toltodik = false;
    }
  }

  get valaszKuldheto(): boolean { return !!this.valasz.trim() && !this.valaszKuld; }

  async valaszol() {
    if (!this.valaszKuldheto || !this.jegy) return;
    this.valaszKuld = true;
    try {
      const fd = new FormData();
      fd.append('szoveg', this.valasz);
      if (this.valaszFajl) fd.append('file', this.valaszFajl, this.valaszFajl.name);
      const r: any = await firstValueFrom(this.http.post(
        `/api/hibajegy/jegyek/${encodeURIComponent(this.jegy.id)}/valasz`, fd, { params: this.params() }));
      this.data.sendToast(r?.uzenet || 'A válaszodat elmentettük.', r?.elkuldve ? 'success' : 'info');
      this.valasz = '';
      this.valaszFajl = null;
      await this.jegyBetolt(this.jegy.id);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A válasz nem sikerült.', 'error');
    } finally {
      this.valaszKuld = false;
    }
  }

  allapotFut = false;

  /** Lezárás / visszanyitás – a Redmine-ban is átáll, naplósorral. A válaszmező szövege megjegyzésként megy. */
  async allapotValt(mit: 'lezar' | 'visszanyit') {
    if (!this.jegy || this.allapotFut) return;
    this.allapotFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/hibajegy/jegyek/${encodeURIComponent(this.jegy.id)}/allapot`,
        { mit, szoveg: (this.valasz || '').trim() }, { params: this.params() }));
      this.data.sendToast(mit === 'lezar' ? `Lezárva (${r?.allapot || 'megoldott'}).` : `Visszanyitva (${r?.allapot || 'új'}).`, 'success');
      this.valasz = '';
      await this.jegyBetolt(this.jegy.id);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az állapot nem váltható.', 'error');
    } finally {
      this.allapotFut = false;
    }
  }

  /** Kézi frissítés a Redmine-ból (a kiszolgáló félpercenként egyszer engedi). */
  async frissit() {
    if (this.frissul) return;
    this.frissul = true;
    try {
      await firstValueFrom(this.http.post('/api/hibajegy/frissites', {}, { params: this.params() }));
      if (this.nezet === 'jegy' && this.jegy) await this.jegyBetolt(this.jegy.id);
      else await this.listaBetolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A frissítés nem sikerült.', e?.status === 429 ? 'info' : 'error');
    } finally {
      this.frissul = false;
    }
  }

  melleklet(c: any) {
    const url = `/api/hibajegy/csatolmany/${encodeURIComponent(c.id)}?intezmeny=${encodeURIComponent(this.intezmeny)}`
      + `&szerep=${encodeURIComponent(this.szerep)}`;
    this.data.megnyitFajl(url, c.fajlnev);
  }

  allapotSzin(j: any): string {
    if (!j) return 'medium';
    if (j.kuldes_allapot && j.kuldes_allapot !== 'elkuldve') return 'warning';
    if (j.lezart) return 'success';
    return 'primary';
  }

  allapotNev(j: any): string {
    if (!j) return '';
    if (j.kuldes_allapot && j.kuldes_allapot !== 'elkuldve') return 'Továbbításra vár';
    return j.allapot || 'Beküldve';
  }

  kategoriaNev(kod: string): string {
    return this.kategoriak.find((k) => k.kod === kod)?.nev || '';
  }

  meret(b: number): string {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} kB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  }
}
